import moment from "moment";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";

const headingSx = {
  fontStyle: "italic",
  fontWeight: 700,
  textTransform: "uppercase",
  color: "primary.dark",
  mt: 3,
  mb: 1,
};

const subHeadingSx = {
  fontWeight: 700,
  mt: 2.5,
  mb: 1,
};

const bodySx = {
  lineHeight: 1.6,
  mb: 1.5,
};

export default function TrialTerms({
  companyName,
  siteName,
  address,
  roleName,
  trialDate,
  trialEnd,
  reportingTo,
  fullName,
}) {
  const letterDate = moment().format("dddd, DD MMMM YYYY");
  const shiftDate = trialDate ? moment(trialDate).format("dddd, DD MMMM YYYY") : "—";
  const startTime = trialDate ? moment(trialDate).format("HH:mm") : "—";
  const endTime = trialEnd ? moment(trialEnd).format("HH:mm") : "—";
  const location = [siteName, address].filter(Boolean).join(", ") || "—";
  const name = fullName || "—";

  return (
    <Box component="article">
      <Typography variant="body1" sx={{ fontWeight: 700 }}>
        {companyName || "—"}
      </Typography>
      {address ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {address}
        </Typography>
      ) : null}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {letterDate}
      </Typography>
      <Typography variant="body1" sx={bodySx}>Dear {name},</Typography>
      <Typography variant="body1" sx={bodySx}>
        Following your recent application for the position of {roleName || "—"}, we would like to invite you to attend a trial shift as part of our recruitment process.
      </Typography>
      <Typography variant="body1" sx={bodySx}>
        The purpose of the trial is to allow both you and the Company an opportunity to assess whether the role is the right fit. During the trial, you will have the opportunity to observe an existing employee, gain an understanding of the role and our working environment, and undertake a limited number of supervised assessment tasks where appropriate. This will allow you to determine whether the responsibilities and expectations of the position are suitable for you, while enabling us to assess your suitability for the role.
      </Typography>

      <Typography variant="h6" sx={headingSx}>Trial Shift Details</Typography>
      <Typography variant="body1" sx={{ mb: 0.5 }}>Date: {shiftDate}</Typography>
      <Typography variant="body1" sx={{ mb: 0.5 }}>Time: {startTime} – {endTime}</Typography>
      <Typography variant="body1" sx={{ mb: 0.5 }}>Location: {location}</Typography>
      <Typography variant="body1" sx={{ mb: 0.5 }}>Duration: 4-6 hours</Typography>
      <Typography variant="body1" sx={{ mb: 1.5 }}>Reporting to: {reportingTo || "—"}</Typography>

      <Typography variant="body1" sx={bodySx}>
        The trial is intended to provide a fair opportunity for both parties to make an informed decision before proceeding further with the recruitment process. It will primarily involve shadowing the designated employee, with any practical activities limited to those reasonably necessary to assess your suitability.
      </Typography>
      <Typography variant="body1" sx={bodySx}>
        You will be supervised throughout the trial and will be expected to follow reasonable instructions and relevant workplace and health and safety requirements.
      </Typography>

      <Typography variant="subtitle1" sx={subHeadingSx}>Payment Arrangements</Typography>
      <Typography variant="body1" sx={bodySx}>
        The trial is intended to be an unpaid recruitment assessment rather than a period of employment or an opportunity for the Company to obtain unpaid labour. The proposed unpaid arrangement is subject to the Company's assessment of the activities involved and the applicable National Minimum Wage requirements. Should payment be legally required, the Company will ensure that the appropriate payment is made. Nothing in this letter waives any statutory entitlement to payment.
      </Typography>

      <Typography variant="subtitle1" sx={subHeadingSx}>Important Information Regarding Employment</Typography>
      <Typography variant="body1" sx={bodySx}>
        Please note that attending or successfully completing the trial does not guarantee an offer of employment. The purpose of the trial is to assess whether the position is suitable for both parties, and the Company reserves the right to decide not to proceed with your application following the assessment.
      </Typography>
      <Typography variant="body1" sx={bodySx}>
        Equally, you are under no obligation to accept any subsequent offer and may decide that the role is not suitable for you. Should the Company wish to offer you a position, this will be communicated separately and confirmed through the appropriate recruitment and employment documentation. You should not assume that an ongoing employment relationship has been agreed simply by attending or completing the trial.
      </Typography>
      <Typography variant="body1" sx={bodySx}>
        Throughout the trial, you will be expected to conduct yourself professionally, respect the confidentiality of any company, employee or customer information you encounter, and comply with the instructions provided by your supervisor. You must not undertake any activity for which you have not received appropriate guidance or authorisation.
      </Typography>
      <Typography variant="body1" sx={bodySx}>
        Either you or the Company may bring the trial to an end before the expected finishing time if necessary. Any payment legally due for time spent undertaking the trial will be dealt with in accordance with applicable law.
      </Typography>
      <Typography variant="body1" sx={bodySx}>
        Please attend the trial in suitable clothing for the position and location. If you have any questions regarding the arrangements or what is required, please contact us in advance.
      </Typography>
      <Typography variant="body1" sx={bodySx}>
        We appreciate your interest in joining the Company and hope the trial will provide you with a useful insight into the position. We look forward to meeting you and wish you the very best during the assessment.
      </Typography>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle1" sx={subHeadingSx}>Candidate Acknowledgement</Typography>
      <Typography variant="body1" sx={bodySx}>
        I confirm that I have read and understood the arrangements outlined in this letter, including the purpose of the trial, the proposed payment arrangements and the fact that completion of the trial does not guarantee an offer of employment. I understand that any statutory entitlement to payment remains unaffected by this acknowledgement.
      </Typography>
    </Box>
  );
}
